import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

/**
 * CarouselArrow, and the dots that go with it (3 Nov 2026).
 *
 * The homepage banner carousel drew its arrows as heavy black discs pinned to
 * the edges of the artwork, competing with the advert they sat on. These are
 * light, frosted discs in the Lusuku slate, set inside the banner, that stay
 * out of the way until the shopper reaches for the carousel: they appear when
 * the pointer is over it or a key moves focus into it, and stay visible on
 * touch screens, where there is no hover to reveal them.
 *
 * Built for react-slick: pass it as `prevArrow` / `nextArrow`. Slick hands it
 * a className (slick-arrow slick-prev / slick-next) and the onClick. Its
 * inline style ({ display: "block" }) is not passed on, since it would undo
 * the centring here, and the other props it hands over (currentSlide,
 * slideCount) are kept off the DOM. The selectors are tripled (&&&) to outrank slick-theme.css, which
 * otherwise repaints the button transparent and draws its own arrow glyph.
 */

const ArrowButton = styled.button`
  &&& {
    position: absolute;
    top: 50%;
    ${(p) => (p.$direction === "prev" ? "left: 18px; right: auto;" : "right: 18px; left: auto;")}
    z-index: 8;
    width: 44px;
    height: 44px;
    margin: 0;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    color: #2f474e;
    box-shadow: 0 6px 18px rgba(8, 22, 26, 0.18), 0 0 0 1px rgba(8, 22, 26, 0.06);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    cursor: pointer;
    opacity: 0;
    transform: translateY(-50%);
    transition: opacity 0.2s ease, transform 0.2s ease, background-color 0.2s ease;
  }

  &&&::before {
    content: none;
    display: none;
  }

  .slick-slider:hover &&&,
  .slick-slider:focus-within &&& {
    opacity: 1;
  }

  &&&:hover {
    background: #ffffff;
    color: #2f474e;
    transform: translateY(-50%) scale(1.06);
  }

  &&&:active {
    transform: translateY(-50%) scale(0.97);
  }

  &&&:focus-visible {
    opacity: 1;
    outline: 3px solid #b9d98f;
    outline-offset: 2px;
  }

  @media (hover: none) {
    &&& {
      opacity: 0.92;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &&& {
      transition: none;
    }
  }

  svg {
    width: 22px;
    height: 22px;
    display: block;
  }
`;

const Chevron = ({ direction }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {direction === "prev" ? <path d="M15 5l-7 7 7 7" /> : <path d="M9 5l7 7-7 7" />}
  </svg>
);

Chevron.propTypes = { direction: PropTypes.oneOf(["prev", "next"]) };

export default function CarouselArrow({ direction = "next", label, className, onClick }) {
  return (
    <ArrowButton
      type="button"
      className={className}
      onClick={onClick}
      $direction={direction}
      aria-label={label || (direction === "prev" ? "Previous" : "Next")}
    >
      <Chevron direction={direction} />
    </ArrowButton>
  );
}

CarouselArrow.propTypes = {
  direction: PropTypes.oneOf(["prev", "next"]),
  label: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

/**
 * The dots for the same carousels: small slate pills, the current one
 * stretched, each with a thin white ring so it reads on light and dark
 * artwork alike. The tap target stays larger than the pill. Spread into the
 * styled wrapper around the slider.
 */
export const carouselDotsStyle = css`
  .slick-dots li {
    width: 12px;
    height: 16px;
    margin: 0 3px;
    transition: width 0.25s ease;
  }

  .slick-dots li button {
    width: 100%;
    height: 100%;
    padding: 0;
  }

  .slick-dots li button:before {
    content: "";
    top: 4px;
    left: 2px;
    right: 2px;
    width: auto;
    height: 8px;
    border-radius: 999px;
    background: rgba(47, 71, 78, 0.38);
    box-shadow: 0 0 0 1.5px rgba(255, 255, 255, 0.85);
    opacity: 1;
  }

  .slick-dots li.slick-active {
    width: 28px;
  }

  .slick-dots li.slick-active button:before {
    background: #2f474e;
    opacity: 1;
  }

  .slick-dots li button:focus-visible:before {
    outline: 2px solid #b9d98f;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    .slick-dots li {
      transition: none;
    }
  }
`;
